import type {
  CreateEventResponse,
  CreatedEndpoint,
  DeliveryWithAttempts,
  Endpoint,
  EventDetail,
  EventSummary,
  Health,
  RelayEvent,
  Stats,
} from '../types';

const DEFAULT_BASE_URL = 'http://localhost:3000';

/** Trailing slashes are stripped so `${base}${path}` never doubles up. */
export const API_BASE_URL: string = (import.meta.env.VITE_API_BASE_URL ?? DEFAULT_BASE_URL).replace(
  /\/+$/,
  '',
);

const API_KEY: string = import.meta.env.VITE_API_KEY ?? '';

/** Write routes are hidden rather than left to fail with a 401. */
export const hasApiKey: boolean = API_KEY.trim().length > 0;

export type ApiErrorKind =
  /** The request never got a response: wrong base URL, API down, CORS, offline. */
  | 'network'
  /** A response arrived with a non-2xx status. */
  | 'http'
  /** A 2xx response that was not the JSON we expected. */
  | 'parse'
  /** Rejected locally before any request was made. */
  | 'config';

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number | null;
  readonly detail: string | null;

  constructor(kind: ApiErrorKind, message: string, status: number | null = null, detail: string | null = null) {
    super(message);
    this.name = 'ApiError';
    this.kind = kind;
    this.status = status;
    this.detail = detail;
  }

  /** Drives the dedicated "can't reach the API" empty state. */
  get isUnreachable(): boolean {
    return this.kind === 'network';
  }
}

export function isAbortError(error: unknown): boolean {
  return typeof error === 'object' && error !== null && (error as { name?: string }).name === 'AbortError';
}

/** Normalises anything thrown by `fetch` or by us into an ApiError. */
export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;
  const message = error instanceof Error ? error.message : String(error);
  return new ApiError('network', 'Something went wrong talking to the Relay API.', null, message);
}

type QueryValue = string | number | boolean | undefined | null;

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const search = params.toString();
  return `${API_BASE_URL}${path}${search ? `?${search}` : ''}`;
}

/** Best-effort extraction of a human-readable reason out of an error response. */
async function readErrorDetail(response: Response): Promise<string | null> {
  try {
    const text = await response.text();
    if (!text) return null;
    try {
      const body: unknown = JSON.parse(text);
      if (typeof body === 'string') return truncate(body);
      if (typeof body === 'object' && body !== null) {
        const record = body as Record<string, unknown>;
        const candidate =
          pickString(record.message) ??
          pickString(record.error) ??
          pickString((record.error as Record<string, unknown> | undefined)?.message) ??
          pickString(record.reason);
        if (candidate) return truncate(candidate);
      }
      return truncate(text);
    } catch {
      return truncate(text);
    }
  } catch {
    return null;
  }
}

function pickString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function truncate(value: string, max = 300): string {
  const trimmed = value.trim();
  return trimmed.length > max ? `${trimmed.slice(0, max)}…` : trimmed;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'DELETE';
  body?: unknown;
  query?: Record<string, QueryValue>;
  signal?: AbortSignal | undefined;
  /** Send the x-api-key header. Required by every write route. */
  auth?: boolean;
  idempotencyKey?: string | undefined;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal, auth = false, idempotencyKey } = options;

  if (auth && !hasApiKey) {
    throw new ApiError(
      'config',
      'No API key configured.',
      null,
      'Set VITE_API_KEY in apps/dashboard/.env.local and restart the dev server. Read-only views work without it.',
    );
  }

  const headers: Record<string, string> = { accept: 'application/json' };
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (auth) headers['x-api-key'] = API_KEY;
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: signal ?? null,
      mode: 'cors',
    });
  } catch (error) {
    // An aborted request is a normal part of polling teardown, not a failure.
    if (isAbortError(error)) throw error;
    throw new ApiError(
      'network',
      `Could not reach the Relay API at ${API_BASE_URL}`,
      null,
      error instanceof Error ? error.message : null,
    );
  }

  if (!response.ok) {
    const detail = await readErrorDetail(response);
    throw new ApiError(
      'http',
      `${response.status} ${response.statusText || 'Request failed'}`,
      response.status,
      detail,
    );
  }

  if (response.status === 204) return undefined as T;

  try {
    return (await response.json()) as T;
  } catch {
    throw new ApiError('parse', 'The API returned a response that was not valid JSON.', response.status);
  }
}

/* ---------------------------------------------------------------------------
 * Reads
 * ------------------------------------------------------------------------ */

export function getHealth(signal?: AbortSignal): Promise<Health> {
  return request<Health>('/health', { signal });
}

export interface ListEventsParams {
  limit?: number;
  cursor?: string | null;
  status?: string | null;
}

/**
 * The pagination field is read as `nextCursor` per the contract, falling back to
 * `nextBefore`, which is what the API currently returns. See README "API contract
 * drift" — the fallback should be deleted once the backend settles on one name.
 */
export async function listEvents(params: ListEventsParams = {}, signal?: AbortSignal) {
  const raw = await request<{
    data?: EventSummary[];
    nextCursor?: string | null;
    nextBefore?: string | null;
  }>('/events', {
    query: { limit: params.limit, cursor: params.cursor, status: params.status },
    signal,
  });

  return {
    data: Array.isArray(raw.data) ? raw.data : [],
    nextCursor: raw.nextCursor ?? raw.nextBefore ?? null,
  };
}

/**
 * Accepts either the contract's `{ event, deliveries }` envelope or the flat
 * `{ ...event, deliveries }` shape the API currently returns, so the signature
 * view cannot silently render blank if the backend lands on the other one.
 */
export async function getEvent(id: string, signal?: AbortSignal): Promise<EventDetail> {
  const raw = await request<
    Partial<EventDetail> & Partial<RelayEvent> & { deliveries?: DeliveryWithAttempts[] }
  >(`/events/${encodeURIComponent(id)}`, { signal });

  const event: RelayEvent = raw.event ?? (raw as unknown as RelayEvent);
  const deliveries = Array.isArray(raw.deliveries) ? raw.deliveries : [];

  return {
    event,
    deliveries: deliveries.map((delivery) => ({
      ...delivery,
      attempts: Array.isArray(delivery.attempts) ? delivery.attempts : [],
    })),
  };
}

export async function listEndpoints(signal?: AbortSignal): Promise<Endpoint[]> {
  const raw = await request<{ data?: Endpoint[] }>('/endpoints', { signal });
  return Array.isArray(raw.data) ? raw.data : [];
}

export interface StatsParams {
  benchmarkRunId?: string | null;
  from?: string | null;
  to?: string | null;
}

export function getStats(params: StatsParams = {}, signal?: AbortSignal): Promise<Stats> {
  return request<Stats>('/stats', {
    query: { benchmarkRunId: params.benchmarkRunId, from: params.from, to: params.to },
    signal,
  });
}

/* ---------------------------------------------------------------------------
 * Writes (x-api-key)
 * ------------------------------------------------------------------------ */

export interface CreateEventInput {
  eventType: string;
  payload: unknown;
  endpointIds?: string[];
  benchmarkRunId?: string;
}

export async function createEvent(
  input: CreateEventInput,
  idempotencyKey?: string,
): Promise<CreateEventResponse> {
  const raw = await request<Partial<CreateEventResponse> & { event?: { id?: string } }>('/events', {
    method: 'POST',
    body: input,
    auth: true,
    idempotencyKey,
  });

  // `eventId` per the contract; `event.id` is what the API currently returns.
  return {
    eventId: raw.eventId ?? raw.event?.id ?? '',
    deliveries: Array.isArray(raw.deliveries) ? raw.deliveries : [],
  };
}

export interface CreateEndpointInput {
  url: string;
  eventTypes?: string[];
  description?: string;
}

export function createEndpoint(input: CreateEndpointInput): Promise<CreatedEndpoint> {
  return request<CreatedEndpoint>('/endpoints', { method: 'POST', body: input, auth: true });
}
